import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { buildLocalOperationTimestamp } from '../utils/localDateTime';
import { 
  Check, Repeat, ArrowRight, RefreshCcw, 
  Landmark, Calendar, FileText, DollarSign, Wallet 
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Módulo de Transferencias Internas - Tesorería
 * Ajustes: Botón confirmar verde a la derecha, sin botón volver, gaps reducidos.
 */

// UUID válido para documento_id (uuid en la base). Usa crypto.randomUUID si existe,
// con fallback v4 manual para contextos donde no esté disponible.
const genUuid = () =>
  (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });

function TesoreriaTransferencia() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [bancos, setBancos] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [historial, setHistorial] = useState([]);
  
  const [form, setForm] = useState({ 
    origen_id: '', 
    destino_id: '', 
    amount: '', 
    obs: '', 
    fecha: new Date().toISOString().split('T')[0] 
  });

  useEffect(() => {
    if (profile?.organization_id) {
        fetchBancos();
        fetchHistorial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const fetchBancos = async () => {
    try {
        const { data, error } = await supabase
            .from('banks')
            .select('*')
            .eq('organization_id', profile.organization_id)
            .eq('is_active', true)
            .order('name');
        
        if (error) throw error;
        setBancos(data || []);
    } catch (error) {
        console.error(error);
        toast.error('Error al cargar cuentas');
    } finally {
        setInitialLoading(false);
    }
  };

  const fetchHistorial = async () => {
    try {
        const today = new Date().toISOString().split('T')[0];
        const start = `${today}T00:00:00.000Z`;
        const end = `${today}T23:59:59.999Z`;

        const { data, error } = await supabase
            .from('movimientos_financieros')
            .select('*, banks(name)')
            .eq('organization_id', profile.organization_id)
            .eq('categoria', 'Transferencia')
            .gte('fecha_operacion', start)
            .lte('fecha_operacion', end)
            .order('created_at', { ascending: false });

        if (error) throw error;
        setHistorial(data || []);
    } catch (error) {
        console.error("Error cargando historial de transferencias", error);
    }
  };

  // KPI: Liquidez Total
  const liquidezTotal = useMemo(() => {
    return bancos.reduce((acc, b) => acc + Number(b.current_balance || 0), 0);
  }, [bancos]);

  const transferir = async (e) => {
    e.preventDefault();
    if (!form.origen_id || !form.destino_id || !form.amount) return toast.error("Completá los campos obligatorios");
    if (form.origen_id === form.destino_id) return toast.error("La cuenta de origen y destino deben ser distintas");
    if (parseFloat(form.amount) <= 0) return toast.error("El monto debe ser mayor a 0");

    const origen = bancos.find(b => b.id === form.origen_id);
    if (!origen) return toast.error("Cuenta origen inválida");
    
    setLoading(true);
    try {
        const orgId = profile.organization_id;
        const monto = parseFloat(form.amount);
        const destino = bancos.find(b => b.id === form.destino_id);

        const nuevoSaldoOrigen = (Number(origen.current_balance) || 0) - monto;
        const nuevoSaldoDestino = (Number(destino.current_balance) || 0) + monto;

        const obsFinal = form.obs ? ` - ${form.obs}` : '';
        // documento_id es UUID en la base: generamos uno real para enlazar las dos patas.
        // (Antes se usaba `TRANSF-<timestamp>`, un texto, y el insert fallaba por tipo uuid,
        //  dejando los saldos movidos pero sin asiento en el libro.)
        const opId = genUuid();
        // Timestamp con offset LOCAL (AR), no Z falso (hora local + sufijo Z=UTC
        // desplazaba la operación 3 h y podía cambiarla de día). Bug T2.
        const fechaOperacion = buildLocalOperationTimestamp(form.fecha);

        // 1. Registrar primero los dos movimientos en el libro. Si esto falla, no se movió plata.
        const { error: movError } = await supabase.from('movimientos_financieros').insert([
            {
                organization_id: orgId,
                bank_id: form.origen_id,
                tipo: 'Egreso',
                categoria: 'Transferencia',
                monto: monto,
                saldo_resultante: nuevoSaldoOrigen,
                descripcion: `Transferencia enviada a ${destino.name}${obsFinal}`,
                fecha_operacion: fechaOperacion,
                documento_tipo: 'Transferencia',
                documento_id: opId,
                user_id: profile?.user_id || null,
                is_active: true
            },
            {
                organization_id: orgId,
                bank_id: form.destino_id,
                tipo: 'Ingreso',
                categoria: 'Transferencia',
                monto: monto,
                saldo_resultante: nuevoSaldoDestino,
                descripcion: `Transferencia recibida de ${origen.name}${obsFinal}`,
                fecha_operacion: fechaOperacion,
                documento_tipo: 'Transferencia',
                documento_id: opId,
                user_id: profile?.user_id || null,
                is_active: true
            }
        ]);
        if (movError) throw movError;

        // 2. Recién con el libro registrado, impactamos los saldos de forma ATÓMICA.
        // Antes eran dos updates ciegos con saldos stale: si el segundo fallaba, el
        // origen quedaba debitado y el destino sin acreditar (plata perdida), con
        // toast de éxito; y dos transferencias concurrentes se pisaban. Bug T8.
        // TODO: mover ambas patas a un único RPC transaccional (transfer_between_banks)
        // para atomicidad total; hoy el error se propaga y se ve en el catch.
        const { error: adjustOrigenError } = await supabase.rpc('adjust_bank_balance', {
            p_bank_id: form.origen_id,
            p_delta: -monto,
        });
        if (adjustOrigenError) throw adjustOrigenError;

        const { error: adjustDestinoError } = await supabase.rpc('adjust_bank_balance', {
            p_bank_id: form.destino_id,
            p_delta: monto,
        });
        if (adjustDestinoError) throw adjustDestinoError;

        toast.success("TRANSFERENCIA EXITOSA");
        setForm({ ...form, amount: '', obs: '' });
        fetchBancos();
        fetchHistorial();

    } catch (e) { 
        console.error(e);
        toast.error("Error al procesar la transferencia"); 
    } finally { 
        setLoading(false); 
    }
  };

  if (initialLoading) {
    return (
      <div className="imp-page imp-container">
        <div className="h-full flex flex-col items-center justify-center p-8">
            <div className="w-11 h-11 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3">
                <RefreshCcw className="text-slate-300 animate-spin" size={18} />
            </div>
            <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">Cargando Cuentas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER TIPO SISTEMA - SIN BOTÓN VOLVER */}
      <div className="imp-header mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Repeat size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Transferencia de Fondos</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Movimiento interno entre cuentas.
            </p>
          </div>
        </div>
      </div>

      {/* KPI SECTION */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Liquidez Total</p>
            <DollarSign size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
             $ {liquidezTotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Fondos disponibles</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cuentas Activas</p>
            <Wallet size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{bancos.length}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Opciones de origen</p>
        </div>
      </div>

      {/* FORM CARD */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Banner visual */}
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center gap-2">
            <Landmark size={16} className="text-[#3FF2AA]" />
            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">
                Detalles de la Operación
            </h2>
        </div>

        <form onSubmit={transferir} className="p-5 space-y-4">
            
            {/* Origen y Destino */}
            <div className="grid grid-cols-1 md:grid-cols-11 gap-3 items-center">
                {/* Origen */}
                <div className="md:col-span-5 space-y-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                        Cuenta Origen (Sale dinero)
                    </label>
                    <div className="relative">
                        <select 
                            className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-2xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner cursor-pointer appearance-none text-slate-700"
                            value={form.origen_id} 
                            onChange={e=>setForm({...form, origen_id:e.target.value})} 
                            required
                        >
                            <option value="">SELECCIONE CUENTA...</option>
                            {bancos.map(b => (
                                <option key={b.id} value={b.id} disabled={b.id === form.destino_id}>
                                    {b.name} — Disponible: $ {Number(b.current_balance || 0).toLocaleString('es-AR')}
                                </option>
                            ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                            <Landmark size={14} className="text-slate-300" />
                        </div>
                    </div>
                </div>

                {/* Flecha (oculta en movil, visible en md) */}
                <div className="hidden md:flex md:col-span-1 justify-center pt-4">
                    <div className="w-8 h-8 bg-slate-50 rounded-full flex items-center justify-center text-[#51A7FD] border border-slate-100 shadow-sm">
                        <ArrowRight size={16} />
                    </div>
                </div>

                {/* Destino */}
                <div className="md:col-span-5 space-y-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                        Cuenta Destino (Entra dinero)
                    </label>
                    <div className="relative">
                        <select 
                            className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-2xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner cursor-pointer appearance-none text-slate-700"
                            value={form.destino_id} 
                            onChange={e=>setForm({...form, destino_id:e.target.value})} 
                            required
                        >
                            <option value="">SELECCIONE CUENTA...</option>
                            {bancos.map(b => (
                                <option key={b.id} value={b.id} disabled={b.id === form.origen_id}>
                                    {b.name} — Actual: $ {Number(b.current_balance || 0).toLocaleString('es-AR')}
                                </option>
                            ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                            <Landmark size={14} className="text-slate-300" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="h-px bg-slate-100 w-full my-2"></div>

            {/* Monto y Fecha */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                        Monto a transferir
                    </label>
                    <div className="relative">
                        <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input 
                            type="number" 
                            step="0.01" 
                            className="w-full pl-10 pr-4 py-3 bg-white border-2 border-slate-100 rounded-2xl text-lg font-black text-slate-700 outline-none focus:border-[#51A7FD] transition-all placeholder:text-slate-200"
                            placeholder="0.00"
                            value={form.amount} 
                            onChange={e=>setForm({...form, amount:e.target.value})} 
                            required 
                        />
                    </div>
                </div>

                <div className="space-y-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                        Fecha de operación
                    </label>
                    <div className="relative">
                        <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input 
                            type="date" 
                            className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold text-slate-700 outline-none focus:border-[#51A7FD] transition-all shadow-inner cursor-pointer uppercase" 
                            value={form.fecha} 
                            onChange={e=>setForm({...form, fecha:e.target.value})} 
                        />
                    </div>
                </div>
            </div>

            {/* Observaciones */}
            <div className="space-y-1">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                    Motivo / Observaciones
                </label>
                <div className="relative">
                    <FileText className="absolute left-4 top-4 text-slate-300" size={16} />
                    <textarea 
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold text-slate-700 outline-none focus:border-[#51A7FD] transition-all shadow-inner min-h-[100px] resize-none uppercase"
                        placeholder="EJ: PAGO DE SERVICIOS, REPOSICIÓN DE CAJA..."
                        value={form.obs} 
                        onChange={e=>setForm({...form, obs:e.target.value})}
                    ></textarea>
                </div>
            </div>

            {/* Botón Acción - Alineado a la derecha, verde estándar */}
            <div className="pt-2 flex justify-end">
                <button 
                    type="submit"
                    disabled={loading} 
                    className="px-10 py-3 rounded-2xl bg-[#3ff2aa] text-[#0B2A36] text-[11px] font-black uppercase tracking-widest shadow-lg shadow-emerald-500/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
                >
                    {loading ? <RefreshCcw className="animate-spin" size={18} /> : <Check size={18} strokeWidth={3} />}
                    {loading ? 'Procesando...' : 'Confirmar Transferencia'}
                </button>
            </div>
        </form>
      </div>

      {/* Historial (Auditoría de Transferencias) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col mt-4 min-h-[300px]">
        <div className="bg-[#0B2A36] px-6 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
            <FileText size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
                Historial de Transferencias (Hoy)
            </span>
            </div>
        </div>

        <div className="overflow-x-auto w-full">
            <table className="w-full text-left min-w-[600px]">
            <thead>
                <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                <th className="px-6 py-4">Fecha / Hora</th>
                <th className="px-6 py-4">Cuenta</th>
                <th className="px-6 py-4">Descripción</th>
                <th className="px-6 py-4 text-right">Monto</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 font-sans">
                {historial.length === 0 ? (
                    <tr>
                        <td colSpan={4} className="py-12 text-center opacity-40 italic text-[10px] font-black uppercase tracking-widest">
                            Sin transferencias registradas hoy
                        </td>
                    </tr>
                ) : (
                    historial.map(h => {
                        const isIngreso = String(h.tipo).toLowerCase().includes('ingreso');
                        return (
                            <tr key={h.id} className="hover:bg-blue-50/20 transition-colors">
                            <td className="px-6 py-4 text-[10px] font-bold text-slate-500">
                                <div className="flex items-center gap-2">
                                    <Calendar size={12} className="text-slate-400" />
                                    {new Date(h.fecha_operacion).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </td>
                            <td className="px-6 py-4 text-[10px] font-black uppercase tracking-tight text-slate-700">
                                {h.banks?.name}
                            </td>
                            <td className="px-6 py-4 text-[10px] font-bold uppercase truncate max-w-[250px] text-slate-700">
                                <span className={`mr-2 px-1.5 py-0.5 rounded text-[8px] font-black ${isIngreso ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                                    {isIngreso ? 'Entrada' : 'Salida'}
                                </span>
                                {h.descripcion || '-'}
                            </td>
                            <td className="px-6 py-4 text-right">
                                <span className={`font-black text-[12px] ${isIngreso ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {isIngreso ? '+' : '-'}${Number(h.monto || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                </span>
                            </td>
                            </tr>
                        );
                    })
                )}
            </tbody>
            </table>
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

export default TesoreriaTransferencia;